import React, {useMemo} from "react"
import {AsyncResultComponent} from "../../../containers/AsyncResult/AsyncResult"
import {
	DataTable,
	DataTableBody,
	TablePending,
	DataTableHead,
	DataTableCell,
	DataTableRow,
} from "../../../components/DataTable/DataTable"
import {AsyncResultRequestState} from "../../../types/asyncResult"
import {ErrorDocument, Pagination} from "../../../resources/common"
import {DataTableActionButton, DataTableActionItem} from "../../../components/DataTable/DataTableActionButton"
import {Tag} from "../../../components/Tag/Tag"
import {EmailTemplate, PaginatedResponse} from "../../../resources/emailTemplates"
import {domainOptions} from "../../../components/DomainFilter/DomainFilter"

// Simple formatting function for dates
const formatDate = (date: Date): string => {
	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	})
}

export const EmailTemplatesDataTable = ({
	result,
	filteredDomains,
	query,
	viewTemplateVersions,
	startEditing,
	publishTemplate,
}: {
	result: AsyncResultRequestState<
		{
			data: PaginatedResponse<EmailTemplate[]>
			pagination: Pagination | undefined
		},
		ErrorDocument
	>
	filteredDomains: string[]
	query: string
	viewTemplateVersions: (template: EmailTemplate) => void
	startEditing: (template: EmailTemplate) => void
	publishTemplate: (templateId: string, version: number) => void
}) => {
	return (
		<AsyncResultComponent asyncResult={result} pendingComponent={<TablePending />}>
			{({value: emailTemplatesResult}) => {
				const templates = emailTemplatesResult.data || []

				// Process the array to group by templateId and keep only the latest version
				const groupedTemplates = Array.isArray(templates)
					? templates.reduce((acc: EmailTemplate[], template: EmailTemplate) => {
							const existingTemplate = acc.find(
								(t: EmailTemplate) => t.attributes.templateId === template.attributes.templateId
							)
							if (!existingTemplate || template.attributes.version > existingTemplate.attributes.version) {
								// Remove existing template if this one is newer
								const filteredAcc = existingTemplate
									? acc.filter((t: EmailTemplate) => t.attributes.templateId !== template.attributes.templateId)
									: acc
								// Add this template
								return [...filteredAcc, template]
							}
							return acc
					  }, [] as EmailTemplate[])
					: []

				// Apply search filter
				const searchFilteredTemplates = useMemo(
					() =>
						query
							? groupedTemplates.filter((template: EmailTemplate) => {
									const searchTerm = query.toLowerCase()
									return template.attributes.templateId.toLowerCase().includes(searchTerm)
							  })
							: groupedTemplates,
					[query, groupedTemplates]
				)

				// Apply domain filter
				const filteredTemplates = useMemo(
					() =>
						filteredDomains.length > 0
							? searchFilteredTemplates.filter((template: EmailTemplate) => {
									return filteredDomains.includes(template.attributes.tags?.domain || "")
							  })
							: searchFilteredTemplates,
					[filteredDomains, searchFilteredTemplates]
				)

				return (
					<DataTable>
						<DataTableHead>
							<DataTableRow>
								<th>ID</th>
								<th>Template ID</th>
								<th>Subject</th>
								<th>Domain</th>
								<th>Latest Version</th>
								<th>Published</th>
								<th>Created At</th>
								<th></th>
							</DataTableRow>
						</DataTableHead>
						<DataTableBody>
							{filteredTemplates.map((template: EmailTemplate) => (
								<DataTableRow
									key={template.id}
									onClick={() => viewTemplateVersions(template)}
									className="clickable-row"
								>
									<DataTableCell>{template.id}</DataTableCell>
									<DataTableCell>{template.attributes.templateId}</DataTableCell>
									<DataTableCell>{template.attributes.subject}</DataTableCell>
									<DataTableCell>
										{template.attributes.tags?.domain && (
											<div className="domain-option-content">
												<div
													className="domain-color-dot"
													style={{
														backgroundColor: domainOptions.find(
															(option) => option.label === template.attributes.tags?.domain
														)?.color,
													}}
												/>
												<span className="domain-option-label">{template.attributes.tags?.domain}</span>
											</div>
										)}
									</DataTableCell>
									<DataTableCell>{template.attributes.version}</DataTableCell>
									<DataTableCell>
										{template.attributes.isPublished ? (
											<Tag size={"small"} content={"Published"} variant={"outline"} theme={"success"} />
										) : (
											<Tag size={"small"} content={"Draft"} variant={"outline"} theme={"dark"} />
										)}
									</DataTableCell>
									<DataTableCell>{formatDate(new Date(template.attributes.createdAt))}</DataTableCell>
									<DataTableActionButton enableActions>
										<DataTableActionItem
											onClick={(e?: React.MouseEvent) => {
												if (e) e.stopPropagation()
												startEditing(template)
											}}
											title={"Edit"}
											icon={"pencil-1--interface-essential"}
										/>
										{!template.attributes.isPublished && (
											<DataTableActionItem
												onClick={(e?: React.MouseEvent) => {
													if (e) e.stopPropagation()
													publishTemplate(template.attributes.templateId, template.attributes.version)
												}}
												title={"Publish"}
												icon={"email-send-1--emails"}
											/>
										)}
									</DataTableActionButton>
								</DataTableRow>
							))}
						</DataTableBody>
					</DataTable>
				)
			}}
		</AsyncResultComponent>
	)
}
